import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  addDays,
  addMonths,
  format,
  getDay,
  isSameMonth,
  startOfMonth,
  subMonths,
} from "date-fns";
import { getCspmChecks } from "../../../../store/cspm/cspmSlice";
import { formatUtcDateSimple } from "../../../../helpers/formatUtcDate";
import { ACCOUNT_DETAIL_FONT_SX } from "../../../../components/AccountDetailPolicy/accountDetailTypography";
import { getScanTimestamp, isCompleteScan } from "./compareUtils";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const ACCENT = "#F98125";

/** Matches Configuration modal header close control. */
const CLOSE_X_STYLE = {
  padding: 0,
  margin: 0,
  border: "none",
  background: "transparent",
  color: "#FFFFFF",
  fontSize: 34,
  fontWeight: 900,
  lineHeight: 0.85,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "none",
  flexShrink: 0,
  WebkitTextStroke: "0.6px #FFFFFF",
};

const pad = (n) => String(n).padStart(2, "0");

const toDayKey = (dateObj) => {
  if (!dateObj) return "";
  return `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())}`;
};

const scanDayKey = (scanDate) => String(scanDate || "").replace("T", " ").slice(0, 10);

const toTitleCase = (str) => {
  if (!str) return "—";
  return String(str)
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const getDayTime = (scanDate) => {
  const time = new Date(String(scanDate).replace(" ", "T")).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const latestDateFromKeys = (availableDates = []) => {
  if (!availableDates.length) return new Date();
  const sorted = [...availableDates].sort(
    (a, b) => new Date(b.replace(" ", "T")) - new Date(a.replace(" ", "T"))
  );
  return new Date(sorted[0].replace(" ", "T"));
};

const formatDayHeading = (dayKey) => {
  if (!dayKey) return "this date";
  const [year, month, day] = dayKey.split("-").map(Number);
  if (!year || !month || !day) return dayKey;
  return format(new Date(year, month - 1, day), "EEEE, MMM d, yyyy");
};

const orderPair = (first, second) => {
  if (!first || !second) return { first, second, swapped: false };
  if (getScanTimestamp(second) < getScanTimestamp(first)) {
    return { first: second, second: first, swapped: true };
  }
  return { first, second, swapped: false };
};

const ScanMonthCalendar = ({
  visibleMonth,
  onMonthChange,
  selectedDayKey,
  scanDayKeys,
  onSelectDay,
  minMonth,
  maxMonth,
}) => {
  const monthStart = startOfMonth(visibleMonth);
  const firstCell = addDays(monthStart, -getDay(monthStart));
  const cells = Array.from({ length: 42 }, (_, index) => addDays(firstCell, index));
  const canPrev = !minMonth || monthStart > startOfMonth(minMonth);
  const canNext = !maxMonth || monthStart < startOfMonth(maxMonth);

  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        bgcolor: "#fff",
        height: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <IconButton
          size="small"
          onClick={() => canPrev && onMonthChange(subMonths(visibleMonth, 1))}
          disabled={!canPrev}
          aria-label="Previous month"
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "0.95rem" }}>
          {format(visibleMonth, "MMMM yyyy")}
        </Typography>
        <IconButton
          size="small"
          onClick={() => canNext && onMonthChange(addMonths(visibleMonth, 1))}
          disabled={!canNext}
          aria-label="Next month"
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.5 }}>
        {WEEKDAYS.map((day) => (
          <Typography
            key={day}
            align="center"
            sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#6b7280", py: 0.5 }}
          >
            {day}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.25 }}>
        {cells.map((day) => {
          const key = toDayKey(day);
          const inMonth = isSameMonth(day, visibleMonth);
          const hasScan = scanDayKeys.has(key);
          const selected = selectedDayKey === key;
          return (
            <Box
              key={`${key}-${inMonth ? "in" : "out"}`}
              component="button"
              type="button"
              disabled={!hasScan}
              aria-pressed={selected}
              aria-label={`${format(day, "MMMM d, yyyy")}${hasScan ? ", has scans" : ""}`}
              onClick={() => hasScan && onSelectDay(day)}
              sx={{
                border: 0,
                bgcolor: selected ? ACCENT : "transparent",
                color: selected ? "#fff" : !inMonth ? "#cbd5e1" : hasScan ? "#111827" : "#94a3b8",
                borderRadius: "8px",
                height: 36,
                cursor: hasScan ? "pointer" : "default",
                fontWeight: selected || hasScan ? 700 : 500,
                fontSize: "0.8rem",
                position: "relative",
                "&:hover": hasScan && !selected ? { bgcolor: "#fff7ed" } : {},
                "&:disabled": { cursor: "default" },
              }}
            >
              {day.getDate()}
              {hasScan && !selected && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 4,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    bgcolor: ACCENT,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
      <Typography sx={{ mt: 1.5, fontSize: "0.72rem", color: "#64748b", lineHeight: 1.4 }}>
        Orange dots mark days with scans. Only those days can be opened.
      </Typography>
    </Box>
  );
};

const PairSlot = ({ label, scan, active, disabled, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      flex: 1,
      minWidth: 0,
      textAlign: "left",
      p: 1.25,
      borderRadius: "10px",
      border: active ? `2px solid ${ACCENT}` : "1px solid #e2e8f0",
      bgcolor: active ? "#fff7ed" : "#fff",
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.65 : 1,
    }}
  >
    <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: active ? ACCENT : "#6b7280", mb: 0.25 }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontWeight: 600,
        fontSize: "0.8rem",
        color: scan ? "#111827" : "#94a3b8",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {scan?.scanDate ? formatUtcDateSimple(scan.scanDate) : "Choose a scan"}
    </Typography>
  </Box>
);

const CompareScanPickerDialog = ({
  open,
  onClose,
  selectedAccount,
  scans = [],
  availableDates = [],
  workloadScheduleName,
  isLoading = false,
  initialFirst,
  initialSecond,
  onConfirmPair,
}) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState("first");
  const [draftFirst, setDraftFirst] = useState(null);
  const [draftSecond, setDraftSecond] = useState(null);
  const [visibleMonth, setVisibleMonth] = useState(() => latestDateFromKeys(availableDates));
  const [selectedDayKey, setSelectedDayKey] = useState(() => toDayKey(latestDateFromKeys(availableDates)));
  const [pendingScanId, setPendingScanId] = useState(null);

  const scanDayKeys = useMemo(
    () => new Set((availableDates || []).map((value) => String(value).replace("T", " ").slice(0, 10))),
    [availableDates]
  );

  const monthBounds = useMemo(() => {
    if (!availableDates.length) return { min: null, max: null };
    const dates = availableDates.map((value) => new Date(String(value).replace(" ", "T")));
    return {
      min: new Date(Math.min(...dates)),
      max: new Date(Math.max(...dates)),
    };
  }, [availableDates]);

  const fetchDay = (dateObj) => {
    if (!selectedAccount || !dateObj) return;
    const dayKey = toDayKey(dateObj);
    const exactDateTime = (availableDates || []).find((dt) => String(dt).startsWith(dayKey));
    if (!exactDateTime) return;
    dispatch(
      getCspmChecks({
        accountNumber: selectedAccount,
        date: exactDateTime,
        ...(workloadScheduleName ? { workloadScheduleName } : {}),
      })
    );
  };

  useEffect(() => {
    if (!open) return;
    const latest = latestDateFromKeys(availableDates);
    setVisibleMonth(latest);
    setSelectedDayKey(toDayKey(latest));
    setDraftFirst(initialFirst || null);
    setDraftSecond(initialSecond || null);
    setStep(initialFirst && !initialSecond ? "second" : "first");
    setPendingScanId(null);
    fetchDay(latest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dayScans = useMemo(
    () =>
      (scans || [])
        .filter((scan) => scan?.scanDate && scanDayKey(scan.scanDate) === selectedDayKey && isCompleteScan(scan))
        .sort((a, b) => getDayTime(b.scanDate) - getDayTime(a.scanDate)),
    [scans, selectedDayKey]
  );

  const otherRunId = step === "first" ? draftSecond?.runId : draftFirst?.runId;
  const pendingScan = dayScans.find((scan) => scan.runId === pendingScanId) || null;

  const previewFirst = step === "first" && pendingScan ? pendingScan : draftFirst;
  const previewSecond = step === "second" && pendingScan ? pendingScan : draftSecond;
  const orderedPreview = orderPair(previewFirst, previewSecond);
  const editingScan = step === "first" ? previewFirst : previewSecond;
  const baselineActive = editingScan
    ? orderedPreview.first?.runId === editingScan.runId
    : step === "first";
  const compareActive = step === "second" && !previewSecond
    ? true
    : Boolean(editingScan && orderedPreview.second?.runId === editingScan.runId);
  const canFinish = Boolean(
    previewFirst?.runId && previewSecond?.runId && previewFirst.runId !== previewSecond.runId
  );

  const handleSelectDay = (day) => {
    setSelectedDayKey(toDayKey(day));
    setPendingScanId(null);
    fetchDay(day);
  };

  const handleStepChange = (nextStep) => {
    if (nextStep === "second" && !draftFirst && !pendingScan) return;
    if (nextStep === "first" && pendingScan && step === "second") {
      setDraftSecond(pendingScan);
    }
    if (nextStep === "second" && pendingScan && step === "first") {
      setDraftFirst(pendingScan);
    }
    setStep(nextStep);
    const nextCurrent = nextStep === "first" ? draftFirst : draftSecond;
    setPendingScanId(
      nextCurrent && scanDayKey(nextCurrent.scanDate) === selectedDayKey ? nextCurrent.runId : null
    );
  };

  const handlePairClick = (slot) => {
    const target = slot === "baseline" ? orderedPreview.first : orderedPreview.second;
    if (!target) {
      handleStepChange(slot === "compare" ? "second" : "first");
      return;
    }
    if (draftFirst?.runId === target.runId || (step === "first" && pendingScan?.runId === target.runId)) {
      handleStepChange("first");
      return;
    }
    handleStepChange("second");
  };

  const handlePrimary = (scanOverride) => {
    const selected = scanOverride || pendingScan;
    const nextFirst = step === "first" && selected ? selected : draftFirst;
    const nextSecond = step === "second" && selected ? selected : draftSecond;

    if (nextFirst?.runId && nextSecond?.runId && nextFirst.runId !== nextSecond.runId) {
      const ordered = orderPair(nextFirst, nextSecond);
      onConfirmPair(ordered.first, ordered.second, ordered.swapped);
      return;
    }

    if (!selected) return;
    if (step === "first") {
      if (draftSecond?.runId === selected.runId) return;
      setDraftFirst(selected);
      setPendingScanId(null);
      setStep("second");
      return;
    }
    if (draftFirst?.runId === selected.runId) return;
    setDraftSecond(selected);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "calc(100vh - 48px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "none",
          border: "1px solid #e2e8f0",
          ...ACCOUNT_DETAIL_FONT_SX,
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: ACCENT,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.125rem", sm: "1.25rem" },
              color: "#FFFFFF",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Select scans to compare
          </Typography>
          <Typography sx={{ mt: 0.75, color: "rgba(255,255,255,0.9)", fontSize: "0.875rem" }}>
            Pick two completed scans in this window. The earlier scan becomes Baseline.
          </Typography>
        </Box>
        <button type="button" aria-label="Close" onClick={onClose} style={CLOSE_X_STYLE}>
          <span aria-hidden="true">×</span>
        </button>
      </Box>

      <DialogContent
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          backgroundColor: "#f3f4f6",
          flex: 1,
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            gap: 1,
            mb: 2,
            p: 1,
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            bgcolor: "#fff",
          }}
        >
          <PairSlot
            label="Baseline"
            scan={orderedPreview.first}
            active={baselineActive}
            onClick={() => handlePairClick("baseline")}
          />
          <Box sx={{ display: "flex", alignItems: "center", color: "#94a3b8", px: 0.5 }}>
            <ArrowForwardIcon fontSize="small" />
          </Box>
          <PairSlot
            label="Compare"
            scan={orderedPreview.second}
            active={compareActive}
            disabled={!draftFirst && !(step === "first" && pendingScan)}
            onClick={() => handlePairClick("compare")}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "300px 1fr" },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          <ScanMonthCalendar
            visibleMonth={visibleMonth}
            onMonthChange={setVisibleMonth}
            selectedDayKey={selectedDayKey}
            scanDayKeys={scanDayKeys}
            onSelectDay={handleSelectDay}
            minMonth={monthBounds.min}
            maxMonth={monthBounds.max}
          />
          <Box
            sx={{
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              bgcolor: "#fff",
              p: 2,
              minHeight: 320,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography sx={{ fontWeight: 700, color: "#111827", mb: 0.25 }}>
              {compareActive ? "Compare scan" : "Baseline scan"} · {formatDayHeading(selectedDayKey)}
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "#64748b", mb: 1.5 }}>
              Account {selectedAccount || "—"}. Select a completed scan for this slot.
            </Typography>
            <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}>
              {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                  <CircularProgress size={32} sx={{ color: ACCENT }} />
                </Box>
              ) : dayScans.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography sx={{ color: "#64748b" }}>No completed scans for this date.</Typography>
                </Box>
              ) : (
                dayScans.map((scan) => {
                  const excluded = Boolean(otherRunId && scan.runId === otherRunId);
                  const selected = pendingScanId === scan.runId;
                  return (
                    <Box
                      key={scan.runId}
                      component="button"
                      type="button"
                      disabled={excluded}
                      onClick={() => !excluded && setPendingScanId(scan.runId)}
                      onDoubleClick={() => !excluded && handlePrimary(scan)}
                      sx={{
                        display: "flex",
                        width: "100%",
                        textAlign: "left",
                        gap: 1.25,
                        mb: 1,
                        p: 1.5,
                        borderRadius: "10px",
                        border: selected ? `2px solid ${ACCENT}` : "1px solid #e2e8f0",
                        bgcolor: selected ? "#fff7ed" : "#fff",
                        cursor: excluded ? "not-allowed" : "pointer",
                        opacity: excluded ? 0.55 : 1,
                        "&:hover": excluded ? {} : { borderColor: ACCENT },
                      }}
                    >
                      <Box sx={{ color: selected ? ACCENT : "#94a3b8", mt: "2px" }}>
                        {selected ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                          <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "0.875rem" }}>
                            {formatUtcDateSimple(scan.scanDate)}
                          </Typography>
                          {selected && (
                            <Chip
                              size="small"
                              label="Selected"
                              sx={{ height: 22, fontWeight: 700, bgcolor: "#ffedd5", color: "#9a3412" }}
                            />
                          )}
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                          <Chip size="small" label={toTitleCase(scan.runType)} sx={{ height: 22, fontWeight: 600 }} />
                          <Chip size="small" label={toTitleCase(scan.scheduleType)} sx={{ height: 22, fontWeight: 600 }} />
                          <Chip
                            size="small"
                            label={scan.resourceScopeType === "Full Run" ? "Full Scan" : scan.resourceScopeType || "—"}
                            sx={{ height: 22, fontWeight: 600 }}
                          />
                          <Chip
                            size="small"
                            label="Complete"
                            sx={{ height: 22, fontWeight: 700, bgcolor: "#dcfce7", color: "#166534" }}
                          />
                          {excluded && (
                            <Chip size="small" label="Already selected" sx={{ height: 22, fontWeight: 600 }} />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: "1px solid #e2e8f0",
          bgcolor: "#fff",
          justifyContent: "space-between",
        }}
      >
        <Button
          variant="contained"
          disableElevation
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            borderRadius: "8px",
            px: 2.5,
            py: 0.85,
            color: "#fff",
            backgroundColor: "#6c757d",
            border: "1px solid #6c757d",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#5a6268",
              borderColor: "#5a6268",
              boxShadow: "none",
            },
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={() => handlePrimary()}
          disabled={!pendingScan && !canFinish}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            backgroundColor: ACCENT,
            boxShadow: "none",
            "&:hover": { backgroundColor: "#e06e14", boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: "#fed7aa", color: "#fff" },
          }}
        >
          {canFinish ? "Compare Scans" : "Continue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompareScanPickerDialog;
